import { useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import useUser from "../../Hook/user";
import { ADS_BANNERS, AdsBannerVariant } from "./config";

const pickRandom = <T,>(items: T[]) =>
  items[Math.floor(Math.random() * items.length)];

const ADS_DISABLED_PAGES = ["/webcams"];

const isPathBanned = (currentPath: string, bannedList?: string[]) => {
  if (!bannedList || bannedList.length === 0) return false;
  return bannedList.some(
    (path) => currentPath === path || currentPath.startsWith(`${path}/`),
  );
};

const isPathAllowed = (currentPath: string, allowedList?: string[]) => {
  if (!allowedList || allowedList.length === 0) return true;
  return allowedList.some(
    (path) => currentPath === path || currentPath.startsWith(`${path}/`),
  );
};

const getBannerPriority = (
  currentPath: string,
  priorities?: Record<string, number>,
) => {
  if (!priorities) return 0;

  const fallbackPriority = priorities["*"] ?? 0;
  const matchedPathPriority = Object.entries(priorities)
    .filter(([path]) => path !== "*")
    .filter(
      ([path]) => currentPath === path || currentPath.startsWith(`${path}/`),
    )
    .reduce(
      (max, [, priority]) => Math.max(max, priority),
      Number.NEGATIVE_INFINITY,
    );

  if (matchedPathPriority === Number.NEGATIVE_INFINITY) return fallbackPriority;
  return matchedPathPriority;
};

type AdsBannersProps = {
  variant?: AdsBannerVariant;
  slot?: string;
};

function AdsBanners({ variant, slot }: AdsBannersProps) {
  const location = useLocation();
  const user = useUser();
  const isGuest = user?.informations?.type === "guest";
  const selectedRef = useRef<AdsBannerVariant | null>(null);
  const lastPathRef = useRef(location.pathname);

  const isEligible = ADS_BANNERS.length > 0;

  const selectedBanner = useMemo(() => {
    if (!isEligible || isGuest) return null;
    if (isPathBanned(location.pathname, ADS_DISABLED_PAGES)) return null;

    const candidates = ADS_BANNERS.filter((banner) => {
      if (variant && banner.id !== variant) return false;
      if (slot && banner.slot && banner.slot !== slot) return false;
      if (!isPathAllowed(location.pathname, banner.allowedPages)) return false;
      if (isPathBanned(location.pathname, banner.bannedFromPages)) return false;
      if (banner.isAvailable) {
        return banner.isAvailable({ user, pathname: location.pathname });
      }
      return true;
    });

    if (!candidates.length) return null;

    const candidatesWithPriority = candidates.map((banner) => ({
      banner,
      priority: getBannerPriority(location.pathname, banner.pagePriorities),
    }));

    const topPriority = candidatesWithPriority.reduce(
      (max, candidate) => Math.max(max, candidate.priority),
      Number.NEGATIVE_INFINITY,
    );

    const topPriorityCandidates = candidatesWithPriority
      .filter((candidate) => candidate.priority === topPriority)
      .map((candidate) => candidate.banner);

    if (!topPriorityCandidates.length) return null;

    if (lastPathRef.current !== location.pathname) {
      selectedRef.current = null;
      lastPathRef.current = location.pathname;
    }

    if (selectedRef.current) {
      return (
        topPriorityCandidates.find(
          (banner) => banner.id === selectedRef.current,
        ) ?? topPriorityCandidates[0]
      );
    }

    const picked = pickRandom(topPriorityCandidates);
    selectedRef.current = picked.id;
    return picked;
  }, [isEligible, isGuest, location.pathname, slot, user, variant]);

  if (!selectedBanner) return null;
  const BannerComponent = selectedBanner.Component;
  return <BannerComponent />;
}

export default AdsBanners;
